import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { SettingsMenuIcon } from '@/assets/icons/menu-icons/settings';
import { BannerDivider, Description, IconWrapper, StatusBannerWrapper, StatusWrapper } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import { updateWizardSteps, WizardProgressBar } from '@/components/basicComponents/wizard/progressBar';
import FooterModal from '@/components/settings-page/importData/footer';
import { getCacheBustedFileUrl } from '@/components/settings-page/utils';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import React from 'react';
import styled from 'styled-components';
import { ManagerSpotlightWizardStepWrapper } from '..';

interface PreviewStepProps {
  managerSpotlightWizardSteps: { name: string; status: string }[];
  firmInfoData: any;
  totalUserCount: number;
  emailPreviewData: any;
  openPage: (path: string) => void;
  sendEmail: () => Promise<void>;
  handleManagerSpotlightWizardSteps: (steps: { name: string; status: string }[]) => void;
  managerSpotlightEmailPreviewLoading: boolean;
}

export const PreviewStep = ({
  managerSpotlightWizardSteps,
  firmInfoData,
  totalUserCount,
  emailPreviewData,
  openPage,
  sendEmail,
  handleManagerSpotlightWizardSteps,
  managerSpotlightEmailPreviewLoading
}: PreviewStepProps) => {
  const { isMobile } = useResponsive();
  const { data: customizationsData } = useCustomizationsData();

  const prevStep = () => {
    updateWizardSteps('prev', managerSpotlightWizardSteps, handleManagerSpotlightWizardSteps);
  };

  return (
    <>
      <ManagerSpotlightWizardStepWrapper isMobile={isMobile}>
        <WizardProgressBar steps={managerSpotlightWizardSteps} />
        <PreviewStepWrapper>
          <StatusBannerWrapper bgColor={themeVar('layer.12')} withBorderRadius>
            <IconWrapper>
              <InfoIcon width={26} height={26} fill={themeVar('font.base')} />
            </IconWrapper>
            <BannerDivider dividerColor={themeVar('font.disabled')} />
            <StatusWrapper>
              <Description color={themeVar('font.base')}>
                Review the notification before sending it. Click back to make any edits. Note that individual strategies will be hidden for any
                advisor that does not have access.
              </Description>
            </StatusWrapper>
          </StatusBannerWrapper>
          <EmailPreviewWrapper>
            <EmailPreview>
              <EmailContainer>
                <EmailHeader>
                  <EmailHeaderLogo src={getCacheBustedFileUrl(customizationsData?.logos?.loginLogo || process.env.REACT_APP_LOGIN_LOGO)} />
                </EmailHeader>
                <EmailBody>
                  <div>
                    <p>
                      Hello <b>{emailPreviewData?.recipientFirstName}</b>,
                    </p>
                    <br />
                    <p>{emailPreviewData?.message}</p>
                  </div>
                  {emailPreviewData?.strategies?.map((strategy: any) => (
                    <React.Fragment key={strategy?.securityStatus}>
                      <div>
                        <StrategyStatus>
                          <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
                            <path
                              d="M30 14.992L26.6667 11.2L27.1335 6.17952L22.2195 5.05749L19.6396 0.716797L15.0041 2.70696L10.3604 0.716797L7.78051 5.05749L2.8665 6.16312L3.33334 11.1836L0 14.992L3.33334 18.7921L2.8665 23.829L7.78051 24.9428L10.3604 29.2835L15.0041 27.277L19.6396 29.2753L22.2195 24.9347L27.1335 23.8126L26.6667 18.7921L30 14.992ZM23.8411 17.8749L24.2015 21.6832L20.4586 22.5022L18.5094 25.7782L14.9877 24.2631L11.466 25.7782L9.5086 22.5022L5.77396 21.6832L6.12612 17.8585L3.6036 14.992L6.12612 12.1009L5.77396 8.3089L9.5086 7.4899L11.466 4.2139L14.9877 5.72088L18.5094 4.20575L20.4586 7.48175L24.2015 8.30075L23.8411 12.1091L26.3718 14.9838L23.8411 17.8749ZM13.62 19.087H16.3473V21.8224H13.62V19.087ZM13.62 8.16968H16.3473V16.3597H13.62V8.16968Z"
                              fill="#2A2E3F"
                            />
                          </svg>
                          {strategy?.securityStatus}
                        </StrategyStatus>
                        <ListInvestmentsWrapper>
                          {strategy?.investments?.map((investment: any) => (
                            <React.Fragment key={guid()}>
                              <StrategyDetails>
                                <b>{investment?.name}</b>
                                {investment?.summary && (
                                  <p>
                                    <b>Summary:</b> {investment?.summary}
                                  </p>
                                )}
                                <DetailsItem>
                                  <ApprovedIconWrapper>
                                    <ApprovedIcon width={23} height={23} fill={themeVar('font.weak')} />
                                  </ApprovedIconWrapper>
                                  <ItemKey>Asset Class:</ItemKey>
                                  {investment?.subAssetClass
                                    ? `${investment?.assetClass?.name} - ${investment?.subAssetClass?.name}`
                                    : investment?.assetClass?.name}
                                </DetailsItem>
                                <DetailsItem>
                                  <ApprovedIconWrapper>
                                    <ApprovedIcon width={23} height={23} fill={themeVar('font.weak')} />
                                  </ApprovedIconWrapper>
                                  <ItemKey>Structure:</ItemKey>
                                  {investment?.pacingType === 'EVERGREEN' && 'Evergreen'}
                                  {investment?.pacingType === 'DRAWDOWN' &&
                                    (investment?.pacingStartingYear ? `Drawdown (${investment.pacingStartingYear})` : 'Drawdown')}
                                </DetailsItem>
                                {!!investment?.sectors && (
                                  <DetailsItem>
                                    <ApprovedIconWrapper>
                                      <ApprovedIcon width={23} height={23} fill={themeVar('font.weak')} />
                                    </ApprovedIconWrapper>
                                    <ItemKey>Sectors:</ItemKey>
                                    {investment?.sectors}
                                  </DetailsItem>
                                )}
                              </StrategyDetails>
                              <ButtonWrapper>
                                <Button size="sm" nonInteractive>
                                  Get Started
                                </Button>
                              </ButtonWrapper>
                            </React.Fragment>
                          ))}
                        </ListInvestmentsWrapper>
                      </div>
                    </React.Fragment>
                  ))}
                  <Divider />
                  <ButtonWrapper>
                    <Button
                      size="sm"
                      styleType="ghost"
                      nonInteractive
                      icon={<SettingsMenuIcon width={12} height={12} fill={themeVar('action.default')} />}
                    >
                      Manage your email preferences
                    </Button>
                  </ButtonWrapper>
                  <YearWrapper>©2024 {firmInfoData?.firmName}, All rights reserved.</YearWrapper>
                </EmailBody>
                <EmailFooter>
                  <FirmWebsite>{firmInfoData?.website}</FirmWebsite>
                </EmailFooter>
              </EmailContainer>
            </EmailPreview>
          </EmailPreviewWrapper>
        </PreviewStepWrapper>
      </ManagerSpotlightWizardStepWrapper>
      <FooterModal
        startOver={prevStep}
        startOverButtonName="Back"
        confirmButton={{
          name: managerSpotlightEmailPreviewLoading ? 'Send Email' : `Send Email to ${totalUserCount} Users`,
          onClick: sendEmail
        }}
      />
    </>
  );
};

const PreviewStepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmailPreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const EmailPreview = styled.div`
  max-width: 1100px;
  width: 100%;
  padding: 50px 20px;
  background: ${themeVar('layer.base')};
  display: flex;
  justify-content: center;
`;

const EmailContainer = styled.div`
  max-width: 600px;
  width: 100%;
  border: 1px solid ${themeVar('border.base')};
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmailHeader = styled.div`
  width: 100%;
  height: 120px;
  background: ${themeVar('loginBg')};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmailHeaderLogo = styled.img`
  width: 160px;
  height: 100px;
  object-fit: contain;
`;

const EmailBody = styled.div`
  padding: 30px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 19px;
  font-weight: 400;
  line-height: 23px;
  color: ${themeVar('font.base')};
`;

const EmailFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 20px 40px;
  height: 70px;
  background: ${themeVar('header.base')};
`;

const EmailFooterLogo = styled.img`
  width: 160px;
  height: 100px;
  object-fit: contain;
`;

const StrategyStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  font-size: 19px;
  font-weight: 600;
  line-height: 23px;
  border-radius: 8px;
  background: ${themeVar('layer.2')};
`;

const ListInvestmentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StrategyDetails = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailsItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  color: ${themeVar('font.strong')};
`;

const ItemKey = styled.span`
  font-weight: 400;
  color: ${themeVar('font.weak')};
`;

const ApprovedIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${themeVar('border.base')};
`;

const YearWrapper = styled.div`
  text-align: center;
  font-size: 10px;
  font-weight: 400;
  line-height: 16px;
  color: ${themeVar('font.weak')};
`;

const FirmWebsite = styled.p`
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  color: ${themeVar('header.action')};
`;
